import React, { useState } from "react";

import { API, graphqlOperation } from "aws-amplify";
import {
  createChargeRule,
  updateChargeRule,
} from "../../../../graphql/mutations";

import { isEmpty, keyBy } from "lodash";

import {
  Backdrop,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  makeStyles,
  Typography,
} from "@material-ui/core";

import {
  ArrayField,
  Button,
  Datagrid,
  FileInput,
  TextField,
  useNotify,
  useRedirect,
} from "react-admin";
import { useForm } from "react-final-form";

import {
  AttachFileIcon,
  ImportIcon,
} from "../../../../components/common/iconography";
import { FileField } from "../../../../components/common/FileField";
import { logError, logHistory } from "../../../../utils/appUtils";
import {
  ChargeRuleHeaderMap,
  MAX_FILE_SIZE,
  MAX_FILE_SIZE_MSG,
  Unit,
  CarrierType,
} from "../../../../utils/constants";
import { csv2arr } from "../../../../utils/fileUtils";
import {
  CancelIcon,
  CloseIcon,
} from "../../../../components/common/iconography";
import { ResourceType } from "../../../../utils/constants";
import { ChargeEvent, ChargeEventChoices } from "./chargeRuleConstants";
import {
  composeNewChargeRule,
  getPeriodicStorageFrequencies,
  isEventDrivenStorageFrequency,
  isPeriodicStorageFrequency,
  normalizeFreeCars,
  normalizeGraduatedRates,
  validateChargeRule,
} from "./ChargeRuleUtils";

// Columns a CSV may leave out. "Free Railcars" was added after accounts had
// exported rule files, so those files still load; an absent column leaves
// the existing value on a replaced rule alone.
const OPTIONAL_COLUMNS = new Set(["freeCars"]);

const useStyles = makeStyles((theme) => ({
  scrollableGrid: {
    overflowX: "auto",
    overflowY: "hidden",
    width: "100%",
    border: "1px solid #ccc",
    borderRadius: 4,
    paddingBottom: 8,
    "&::-webkit-scrollbar": {
      height: "8px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#888",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "#555",
    },
  },
  dialogContent: {
    // https://stackoverflow.com/questions/47698037/how-can-i-set-a-height-to-a-dialog-in-material-ui
    height: "90vh",
  },
  dialogTitle: {
    display: "flex",
    paddingTop: "5px",
    paddingBottom: "5px",
    justifyContent: "space-between",
  },
}));

const validateImportedRateTiers = (rates) => {
  if (!Array.isArray(rates)) return '"Graduated Rates" must be a JSON array.';
  if (rates.length === 0) return undefined;

  for (const [index, tier] of rates.entries()) {
    const isFirstRow = index === 0;
    const isLastRow = index === rates.length - 1;
    const rawDays = tier?.days;
    const rawRate = tier?.rate;

    const hasEmptyDays = rawDays === "" || rawDays == null;
    const hasEmptyRate = rawRate === "" || rawRate == null;
    const parsedDays = hasEmptyDays ? null : Number(rawDays);
    const parsedRate = hasEmptyRate ? null : Number(rawRate);

    if (isFirstRow) {
      if (!hasEmptyRate) {
        return "The first graduated rate row must have a blank/null rate.";
      }
    } else if (!Number.isFinite(parsedRate) || parsedRate < 0) {
      return "All graduated rate rows after the first must have a valid rate.";
    }

    if (hasEmptyDays) {
      if (rates.length === 1 || !isLastRow) {
        return 'Only the last graduated rate row may have an empty "days" value.';
      }
    } else if (!Number.isFinite(parsedDays) || parsedDays <= 0) {
      return 'Each graduated rate "days" value must be greater than 0.';
    }
  }

  return undefined;
};

const getRuleMergeKey = (rule) => `${rule?.event}_${rule?.serviceId}`;

const validateCanonicalPeriodicStorageFrequency = (
  candidateRules,
  csvRowsByMergeKey,
  errors,
) => {
  const periodicFrequencies = getPeriodicStorageFrequencies(candidateRules);
  if (periodicFrequencies.length <= 1) return;

  const frequencyList = periodicFrequencies.join(", ");
  candidateRules.forEach((rule) => {
    if (!isPeriodicStorageFrequency(rule?.frequency)) return;
    const csvRow = csvRowsByMergeKey[getRuleMergeKey(rule)];
    if (!csvRow) return;
    csvRow.error = true;
    csvRow.conflictError =
      "Account must use one canonical periodic storage frequency.";
  });
  errors.push(
    `Storage rules must use one canonical periodic frequency per account. Found: ${frequencyList}.`,
  );
};

const validateCSV = (
  csvArray,
  errors,
  existingServices,
  existingRules = [],
) => {
  let missingEvent = false;
  let invalidEvent = false;
  let missingServiceName = false;
  let missingBillingUOM = false;
  let invalidBillingUOM = false;
  let invalidCommodity = false;
  let missingCarrierForShipmentUom = false;
  let invalidFreeCars = false;
  let freeCarsOffRail = false;
  let invalidCarrierForUom = false;

  const validEvents = ChargeEventChoices.map((itm) => itm.id);
  const validBillingUOMs = Object.values(Unit);
  const loadTypeMap = window.appUser.reloadYard.yardCommodityMap || {};
  const services = existingServices || [];
  if (!services || services.length === 0) {
    errors.push("No services found. Ensure services are loaded.");
    return errors;
  }

  debugger;
  csvArray.forEach((row) => {
    if (row["event"] === "") {
      missingEvent = true;
      row.error = true;
    } else if (!validEvents.includes(row["event"])) {
      invalidEvent = true;
      row.error = true;
    }

    if (row["serviceName"] === "") {
      missingServiceName = true;
      row.error = true;
    } else {
      // Check if service name exists
      const service = services.find((s) => s.name === row["serviceName"]);
      if (!service) {
        row.error = true;
        errors.push(`Service "${row["serviceName"]}" not found.`);
      } else {
        // Store the service ID for later use
        row["serviceId"] = service.id;
      }
    }

    if (row["billingUOM"] === "") {
      missingBillingUOM = true;
      row.error = true;
    } else if (!validBillingUOMs.includes(row["billingUOM"])) {
      invalidBillingUOM = true;
      row.error = true;
    }

    // Validate carrierType against shipment-level UOMs (CAR/TL)
    const uom = row["billingUOM"];
    const carrierType = row["carrierType"];
    if (uom === Unit.CAR) {
      if (!carrierType) {
        missingCarrierForShipmentUom = true;
        row.error = true;
      } else if (carrierType !== CarrierType.RAIL) {
        invalidCarrierForUom = true;
        row.error = true;
      }
    } else if (uom === Unit.TL) {
      if (!carrierType) {
        missingCarrierForShipmentUom = true;
        row.error = true;
      } else if (carrierType !== CarrierType.TRUCK) {
        invalidCarrierForUom = true;
        row.error = true;
      }
    }

    // check for correct commodities
    if (row["reloadYardCommodityId"]?.trim() !== "") {
      const ryCommodity = loadTypeMap[row["reloadYardCommodityId"]];
      if (!ryCommodity) {
        row.error = true;
        invalidCommodity = true;
      } else {
        row["reloadYardCommodityId"] = ryCommodity.id;
      }
    }

    // check numeric fields
    if (row["freeDays"] !== "" && row["freeDays"] !== undefined) {
      const freeDays = Number(row["freeDays"]);
      if (isNaN(freeDays) || freeDays < 0) {
        row.error = true;
      }
    }

    if (row["freeCars"] !== "" && row["freeCars"] !== undefined) {
      const freeCars = Number(row["freeCars"]);
      if (!Number.isInteger(freeCars) || freeCars < 0) {
        row.error = true;
        invalidFreeCars = true;
      } else if (
        freeCars > 0 &&
        (row["event"] !== ChargeEvent.STORAGE ||
          carrierType !== CarrierType.RAIL ||
          isEventDrivenStorageFrequency(row["frequency"]))
      ) {
        row.error = true;
        freeCarsOffRail = true;
      }
    }

    if (row["minCharge"] !== "" && row["minCharge"] !== undefined) {
      const minCharge = Number(row["minCharge"]);
      if (isNaN(minCharge) || minCharge < 0) {
        row.error = true;
      }
    }

    if (row["maxCharge"] !== "" && row["maxCharge"] !== undefined) {
      const maxCharge = Number(row["maxCharge"]);
      if (isNaN(maxCharge) || maxCharge < 0) {
        row.error = true;
      }
    }

    if (row["graduatedRates"]?.trim()) {
      try {
        const parsedRates = JSON.parse(row["graduatedRates"]);
        const ratesError = validateImportedRateTiers(parsedRates);
        if (ratesError) {
          row.error = true;
          errors.push(`${ratesError} Service "${row["serviceName"]}".`);
          return;
        }
        const normalizedRates = normalizeGraduatedRates(parsedRates);
        row["graduatedRates"] = normalizedRates;
      } catch (e) {
        row.error = true;
        errors.push(
          `Invalid JSON in "Graduated Rates" for service "${row["serviceName"]}".`,
        );
      }
    }
  });

  // Conflict detection: validates imported rules against each other AND against
  // existing account rules that won't be replaced by the import.
  const validRules = csvArray.filter((row) => !row.error && row.serviceId);
  if (validRules.length > 0) {
    const mockAccount = {
      id: "temp_account_id",
      groups: "temp_groups",
      reloadYardId: window.appUser?.reloadYardId,
    };

    // Convert CSV rows to rule objects using composeNewChargeRule
    const ruleObjects = validRules.map((row) => {
      // Create a temporary item object that matches what composeNewChargeRule expects
      const tempItem = {
        ...row,
        name: row.serviceName, // composeNewChargeRule looks for item.name to find service
      };

      const composedRule = composeNewChargeRule(
        tempItem,
        services,
        mockAccount,
      );

      // Add a temporary ID for conflict detection
      return {
        ...composedRule,
        id: `temp_${row.event}_${row.serviceId}_${row.areaId || "null"}_${
          row.zoneId || "null"
        }_${row.carrierType || "null"}_${row.reloadYardCommodityId || "null"}`,
      };
    });

    // Check each rule against all other rules for conflicts
    const importedRulesByMergeKey = keyBy(validRules, getRuleMergeKey);
    const retainedExistingRules = (existingRules || []).filter(
      (rule) => !importedRulesByMergeKey[getRuleMergeKey(rule)],
    );

    ruleObjects.forEach((rule, index) => {
      const otherImportedRules = ruleObjects.filter((_, i) => i !== index);
      const allRulesToCheck = [...otherImportedRules, ...retainedExistingRules];
      const validationErrors = validateChargeRule(rule, allRulesToCheck);

      if (validationErrors._error) {
        const csvRow = validRules[index];
        csvRow.error = true;
        csvRow.conflictError = validationErrors._error;
        errors.push(`Row ${index + 1}: ${validationErrors._error}`);
      }
    });

    const candidateRules = [...retainedExistingRules, ...validRules].filter(
      (rule) => rule?.event === ChargeEvent.STORAGE,
    );
    validateCanonicalPeriodicStorageFrequency(
      candidateRules,
      importedRulesByMergeKey,
      errors,
    );
  }

  if (missingEvent) errors.push(`Missing "Event" entries.`);
  if (invalidEvent)
    errors.push(
      `Invalid "Event" entries. Must be INBOUND, OUTBOUND, or STORAGE.`,
    );

  if (missingServiceName) errors.push(`Missing "Service Name" entries.`);

  if (missingBillingUOM) errors.push(`Missing "Units of Measure" entries.`);
  if (invalidBillingUOM) errors.push(`Invalid "Units of Measure" entries.`);
  if (missingCarrierForShipmentUom)
    errors.push(`Missing "Carrier Type" for shipment-level UOMs (CAR/TL).`);
  if (invalidCarrierForUom)
    errors.push(
      `Invalid "Carrier Type" for UOM: CAR requires RAIL, TL requires TRUCK.`,
    );

  if (invalidCommodity) errors.push(`Invalid "Load Type" entries.`);

  if (invalidFreeCars)
    errors.push(
      `Invalid "Free Railcars" entries. Must be a whole number, 0 or more.`,
    );
  if (freeCarsOffRail)
    errors.push(
      `"Free Railcars" only apply to STORAGE rules with Carrier Type RAIL on a periodic storage frequency.`,
    );

  return errors;
};

// Exported for the header-alignment tests; the dialog is its only caller.
export const processCSV = async (
  str,
  delim = ",",
  setCSVData,
  setCSVErrors,
  existingServices,
  existingRules,
  notify,
) => {
  let errors = [];
  let lengthError = false;

  try {
    let headers = str
      .slice(0, str.indexOf("\n"))
      .replace("\r", "")
      .split(delim);
    // Trailing empties only: exported files often end the header line with a
    // comma. Dropping an empty cell from the middle would shift every column
    // after it out of step with the row values below.
    while (headers.length && headers[headers.length - 1].trim() === "")
      headers.pop();
    const hlen = headers.length;

    // Positional: one slot per CSV column, null where the header is not one of
    // ours. Row values are read by index, so a recognized header has to keep
    // the position it occupies in the file — packing only the recognized ones
    // into a shorter array would slide every later value into the wrong field.
    const headerColumns = headers.map(
      (h) => ChargeRuleHeaderMap[h.trim()] || null, // Trim header names
    );
    const seenColumns = new Set();
    const duplicateHeader = headerColumns.some((col) => {
      if (!col) return false;
      if (seenColumns.has(col)) return true;
      seenColumns.add(col);
      return false;
    });
    // An unknown or repeated column is rejected rather than ignored: its cells
    // still sit in every row, so accepting the file would misassign values.
    const unknownHeader = headerColumns.some((col) => !col);
    const missingHeader = Object.values(ChargeRuleHeaderMap).some(
      (col) => !OPTIONAL_COLUMNS.has(col) && !headerColumns.includes(col),
    );
    const invalidHeaders = unknownHeader || duplicateHeader || missingHeader;
    if (invalidHeaders) {
      notify("Modified or unrecognized column headers.", "warning");
      setCSVErrors(["Modified or unrecognized column headers."]);
      setCSVData({});
      return;
    }

    let rows = csv2arr(str.slice(str.indexOf("\n") + 1));
    rows = rows.filter((row) => row.join("") !== "");

    let newArray = rows
      .map((row) => {
        if (!isEmpty(row) && row.length > 1) {
          const eachObject = headerColumns.reduce((obj, header, i) => {
            if (header) obj[header] = (row[i] || "").trim(); // Trim each cell value
            return obj;
          }, {});

          if (row.length > hlen) {
            eachObject["error"] = true;
            lengthError = true;
          }

          return eachObject;
        }
        return null; // Explicitly return null for rows that don't meet the condition
      })
      .filter((itm) => itm);

    if (lengthError)
      errors.push(
        `Values in extra columns, or extra unquoted commas within entries.`,
      );
    errors = validateCSV(newArray, errors, existingServices, existingRules);
    if (errors.length > 0) {
      setCSVErrors(errors);
      newArray = newArray.filter((row) => row.error);
      setCSVData({ preview: newArray.slice(0, 10) });
    } else {
      setCSVData({ rules: newArray, preview: newArray.slice(0, 10) });
      notify("Billing Rules uploaded successfully.", "info");
    }
  } catch (err) {
    logError("accounts:ChargeRuleUpload:processCSV", err, notify);
  }
};

const ErrorTextField = (props) => (
  <TextField
    color={props.record.error ? "error" : "body"}
    {...props}
    title={
      props.record.conflictError ||
      (props.record.error ? "Validation error" : "")
    }
  />
);

export const ChargeRuleUpload = ({
  csvData,
  setCSVData,
  csvErrors,
  setCSVErrors,
  existingServices,
  existingRules,
  ...props
}) => {
  const classes = useStyles();
  const form = useForm();
  const notify = useNotify();

  const handleCSVFile = async (value) => {
    try {
      setCSVErrors(null);
      if (!value) {
        form.change("chargeRulesFile", null);
        setCSVData({});
      } else if (value?.size > MAX_FILE_SIZE) {
        notify(MAX_FILE_SIZE_MSG, "warning");
        form.change("chargeRulesFile", null);
        setCSVData({});
      } else {
        const fileState = form.getFieldState("chargeRulesFile");
        const reader = new FileReader();
        reader.onload = function (e) {
          const text = e.target.result;
          processCSV(
            text,
            ",",
            setCSVData,
            setCSVErrors,
            existingServices,
            existingRules,
            notify,
          );
        };
        let blob = await fetch(fileState.value.src).then((r) => r.blob());
        reader.readAsText(blob);
      }
    } catch (error) {
      logError("accounts:ChargeRuleUpload:handleCSVFile", error, notify);
    }
  };

  return (
    <Grid container direction="column" spacing={1} fullWidth>
      <Grid container direction="row" spacing={4}>
        <Grid item xs={12} md={6}>
          <Typography variant="overline" color="textSecondary" gutterBottom>
            Instructions
          </Typography>
          <ol>
            <li>Export existing Billing Rules from another account.</li>
            <li>Do not modify the column headers or the data.</li>
            <li>Upload the CSV file here.</li>
            <li> For help, contact us at 877-TRANSLOAD / 877-872-6756 </li>
          </ol>
        </Grid>
        <Grid item xs={12} md={6}>
          <FileInput
            source="chargeRulesFile"
            label="BILLING RULES IMPORT"
            variant="outlined"
            fullWidth
            placeholder={
              <p>
                <AttachFileIcon fontSize="large" /> <br /> Drop a CSV File to
                upload, or click to select it.
              </p>
            }
            accept=".csv"
            onChange={handleCSVFile}
            helperText={false}
          >
            <FileField source="src" title="title" target="_blank" />
          </FileInput>
        </Grid>
      </Grid>
      <Grid container direction="row" spacing={1}>
        {csvErrors && (
          <Grid item xs={12}>
            <Typography variant="overline" color="error">
              Loading Errors:
            </Typography>
            <ul>
              {csvErrors.map((error, index) => (
                <Typography key={index} color="error">
                  <li>{error}</li>
                </Typography>
              ))}
            </ul>
          </Grid>
        )}
        {csvData?.preview?.length > 0 && (
          <>
            <Grid item xs={12}>
              <Typography variant="overline" color="textSecondary" gutterBottom>
                Upload Preview (top {`${csvData.preview.length}`} rows):
              </Typography>
            </Grid>
            <Grid item xs={12} className={classes.scrollableGrid}>
              <ArrayField source="preview" record={csvData} fullWidth>
                <Datagrid>
                  <ErrorTextField source="event" label="Event" />
                  <ErrorTextField source="serviceName" label="Service Name" />
                  <ErrorTextField source="billingUOM" label="UOM" />
                  <ErrorTextField source="carrierType" label="Carrier Type" />
                  <ErrorTextField
                    source="reloadYardCommodityId"
                    label="Load Type"
                  />
                  <ErrorTextField
                    source="frequency"
                    label="Storage Frequency"
                  />
                  <ErrorTextField source="feeAccrual" label="Fee Accrual" />
                  <ErrorTextField
                    source="graduatedRates"
                    label="Graduated Rates"
                  />
                  <ErrorTextField source="freeDays" label="Free Days" />
                  <ErrorTextField source="freeCars" label="Free Railcars" />
                </Datagrid>
              </ArrayField>
            </Grid>
          </>
        )}
      </Grid>
    </Grid>
  );
};

export const ChargeRuleUploadDialog = ({
  account,
  existingRules,
  existingServices,
  uploadOpen,
  setUploadOpen,
  handleRefresh,
  ...props
}) => {
  const classes = useStyles();
  const form = useForm();
  const notify = useNotify();
  const redirect = useRedirect();
  const [csvData, setCSVData] = useState({});
  const [csvErrors, setCSVErrors] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleRulesUpload = async () => {
    if (csvData.rules?.length > 0) {
      try {
        setIsSaving(true);
        const oldRulesMap =
          existingRules?.length > 0
            ? keyBy(existingRules, (rule) => `${rule.event}_${rule.serviceId}`)
            : {};

        const results = await Promise.allSettled(
          csvData.rules.map((item) => {
            // check old vs new rules using serviceId (which was set during validation)
            const ruleKey = `${item["event"]}_${item["serviceId"]}`;
            const oldRule = oldRulesMap[ruleKey];

            if (oldRule) {
              const input = { ...oldRule, ...item };
              // History will be logged via logHistory after successful update

              input.minCharge = input.minCharge || null;
              input.maxCharge = input.maxCharge || null;
              input.freeDays = input.freeDays ?? null;
              input.freeCars = normalizeFreeCars(input);
              input.graduatedRates = normalizeGraduatedRates(
                input.graduatedRates,
              );

              delete input.createdAt;
              delete input.error;
              delete input.history;
              delete input.service;
              delete input.serviceName;
              delete input.updatedAt;

              return API.graphql(graphqlOperation(updateChargeRule, { input }));
            } else {
              // History will be logged via logHistory after successful creation
              const input = composeNewChargeRule(
                item,
                existingServices,
                account,
              );
              return API.graphql(graphqlOperation(createChargeRule, { input }));
            }
          }),
        );

        const resError = results.find((itm) => itm.status === "rejected");
        if (resError) throw resError.reason;

        const newRules = [];
        const updatedRuleMap = {};
        results.forEach(
          (result) =>
            result.value.data.createChargeRule &&
            newRules.push(result.value.data.createChargeRule),
        );
        results.forEach((result) => {
          if (result.value.data.updateChargeRule)
            updatedRuleMap[result.value.data.updateChargeRule.id] =
              result.value.data.updateChargeRule;
        });

        let newChargeRuleList = existingRules.map((itm) =>
          updatedRuleMap[itm.id] ? updatedRuleMap[itm.id] : itm,
        );
        newChargeRuleList = [...newChargeRuleList, ...newRules];
        form.change("chargeRules.items", newChargeRuleList);

        // Log history for successfully created and updated rules
        for (const rule of newRules) {
          const historyNote = {
            parentId: rule.id,
            parentType: ResourceType.CHARGE_RULE,
            text: "Rule Created.",
          };
          logHistory(historyNote);
        }

        for (const rule of Object.values(updatedRuleMap)) {
          const historyNote = {
            parentId: rule.id,
            parentType: ResourceType.CHARGE_RULE,
            text: "Billing Rules bulk update",
          };
          logHistory(historyNote);
        }

        notify("Rules imported successfully", "success");
      } catch (error) {
        logError(
          "accounts:AccountEdit:handleRulesUpload",
          error,
          notify,
          redirect,
        );
      } finally {
        handleRefresh && handleRefresh();
        setIsSaving(false);
      }
    }
    handleClose();
  };

  const handleClose = () => {
    setCSVData({});
    setCSVErrors(null);
    setUploadOpen(false);
  };

  return (
    <Dialog maxWidth="xl" open={uploadOpen} fullWidth>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <h2> Billing Rules Import </h2>
        <IconButton aria-label="close" onClick={handleClose}>
          <CloseIcon fontSize="large" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers className={classes.dialogContent}>
        <Backdrop className={classes.backdrop} open={isSaving}>
          <CircularProgress color="inherit" />
        </Backdrop>
        <ChargeRuleUpload
          csvData={csvData}
          setCSVData={setCSVData}
          csvErrors={csvErrors}
          setCSVErrors={setCSVErrors}
          existingServices={existingServices}
          existingRules={existingRules}
        />
      </DialogContent>
      <DialogActions>
        <Button label="Cancel" color="default" autoFocus onClick={handleClose}>
          <CancelIcon />
        </Button>
        <Button
          label="Import"
          color="primary"
          onClick={handleRulesUpload}
          disabled={csvErrors || isSaving}
        >
          <ImportIcon />
        </Button>
      </DialogActions>
    </Dialog>
  );
};
